import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { AppShell } from '@/components/layout/AppShell'
import { RequireAuth } from '@/components/auth/RequireAuth'
import { LoginPage } from '@/features/auth/components/LoginPage'
import { IngestPage } from '@/features/ingest/components/IngestPage'
import { CardLibrary } from '@/features/cards/components/CardLibrary'
import { CardDetail } from '@/features/cards/components/CardDetail'
import { SourceDetail } from '@/features/sources/components/SourceDetail'
import { Dashboard } from '@/features/dashboard/components/Dashboard'
import { ReviewQueue } from '@/features/cards/components/ReviewQueue'
import { DevelopPage } from '@/features/develop/components/DevelopPage'
import { ZinsserPage } from '@/features/zinsser/components/ZinsserPage'
import { CpPage } from '@/features/cp/components/CpPage'
import { ContentCpPage } from '@/features/content_cp/components/ContentCpPage'
import { FPage } from '@/features/f/components/FPage'
import { ContentFPage } from '@/features/content_f/components/ContentFPage'
import { ContentDPage } from '@/features/content_d/components/ContentDPage'
import { RequireAuthZ } from '@/features/z/components/RequireAuthZ'
import { DPage } from '@/features/d/components/DPage'
import { DLoginPage } from '@/features/d/components/DLoginPage'
import { DPendingApprovalPage } from '@/features/d/components/DPendingApprovalPage'
import { RequireAuthD } from '@/features/d/components/RequireAuthD'
import { AdminApprovalPage } from '@/features/admin/components/AdminApprovalPage'
import { EvalsPage } from '@/features/evals/components/EvalsPage'
import { PrivacyPage } from '@/features/legal/components/PrivacyPage'
import { CommissioningSurface } from '@/features/author'
import { DocumentList, DocEditor, PublicViewer } from '@/features/graph'
import { StudioPage } from '@/features/studio'
import { ConciergePage } from '@/features/concierge'
import { CockpitPage } from '@/features/cockpit'
import { VaultPage } from '@/features/vault'

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Auth routes */}
        <Route path="login" element={<LoginPage />} />

        {/* Zinsser is standalone and protected - no AppShell header */}
        <Route
          path="zinsser"
          element={
            <RequireAuth>
              <ZinsserPage />
            </RequireAuth>
          }
        />

        {/* Studio — calendar-first block editor with agent chat. Standalone (no AppShell). */}
        <Route
          path="studio"
          element={
            <RequireAuth>
              <StudioPage />
            </RequireAuth>
          }
        />

        {/* Concierge — chat-with-the-agent. Standalone (no AppShell).
            Sibling to the Obsidian plugin; same backend, web-native UI. */}
        <Route
          path="concierge"
          element={
            <RequireAuth>
              <ConciergePage />
            </RequireAuth>
          }
        />

        {/* Cockpit — mobile-first server-direct dashboard + quick capture.
            Standalone (no AppShell). Same payload the Obsidian plugin's
            CockpitView renders; the phone's home-screen surface (PWA). */}
        <Route
          path="cockpit"
          element={
            <RequireAuth>
              <CockpitPage />
            </RequireAuth>
          }
        />

        {/* Vault — the three-pane workspace (Obsidian-replacement). Standalone.
            Tree + fractal pages over the vault_nodes projection + Concierge. */}
        <Route
          path="vault"
          element={
            <RequireAuth>
              <VaultPage />
            </RequireAuth>
          }
        />

        {/* Admin routes (no auth required - uses token verification) */}
        <Route path="admin/approve" element={<AdminApprovalPage />} />

        {/* Evals dashboard (no auth required - internal tool) */}
        <Route path="evals" element={<EvalsPage />} />

        {/* Legal pages (public) */}
        <Route path="privacy" element={<PrivacyPage />} />

        {/* Author commissioning — public, no auth required */}
        <Route path="commission" element={<CommissioningSurface />} />

        {/* JSON Graph — M2: Supabase-backed, per-doc routing, public shares */}
        <Route
          path="graph"
          element={
            <RequireAuth>
              <DocumentList />
            </RequireAuth>
          }
        />
        <Route
          path="graph/:id"
          element={
            <RequireAuth>
              <DocEditor />
            </RequireAuth>
          }
        />
        {/* Public share viewer — no auth */}
        <Route path="g/:slug" element={<PublicViewer />} />

        {/* CP (Commonplace) - uses same auth as Z */}
        <Route
          path="cp"
          element={
            <RequireAuthZ>
              <CpPage />
            </RequireAuthZ>
          }
        />

        {/* F (Foolscap) - uses same auth as Z */}
        <Route
          path="f"
          element={
            <RequireAuthZ>
              <FPage />
            </RequireAuthZ>
          }
        />

        {/* Content CP (Content Commonplace) - uses same auth as Z */}
        <Route
          path="content_cp"
          element={
            <RequireAuthZ>
              <ContentCpPage />
            </RequireAuthZ>
          }
        />

        {/* Content F (Content Foolscap) - uses same auth as Z */}
        <Route
          path="content_f"
          element={
            <RequireAuthZ>
              <ContentFPage />
            </RequireAuthZ>
          }
        />

        {/* Content D (Content Drafter) - uses same auth as Z */}
        <Route
          path="content_d"
          element={
            <RequireAuthZ>
              <ContentDPage />
            </RequireAuthZ>
          }
        />

        {/* D (Drafter) auth and app routes */}
        <Route path="d/login" element={<DLoginPage />} />
        <Route path="d/pending" element={<DPendingApprovalPage />} />
        <Route
          path="d"
          element={
            <RequireAuthD>
              <DPage />
            </RequireAuthD>
          }
        />

        {/* All other routes use AppShell with header - public */}
        <Route element={<AppShell />}>
          <Route index element={<IngestPage />} />
          <Route path="build" element={<DevelopPage />} />
          <Route path="cards" element={<CardLibrary />} />
          <Route path="cards/:cardId" element={<CardDetail />} />
          <Route path="sources/:sourceId" element={<SourceDetail />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="review" element={<ReviewQueue />} />
        </Route>
      </Routes>
    </BrowserRouter>
  )
}
